import React from "react";
import { SPOTTER_WORDMARK } from "./config";
import { ModeDot } from "./fields/modeColors";

/**
 * Central home for every piece of user-facing copy in the `spotter` feature (header, view
 * picker/changes panel chrome, Fields, Facial Recognition, Rename) — one file to edit wording in
 * without hunting through JSX across `FieldsScreen.tsx`/`FrScreen.tsx`/`RenameScreen.tsx`/
 * `SpotterHeader.tsx`/`SpotterChangesPanel.tsx`. Structural/technical strings are NOT here: mode
 * identifiers ("direct"/"match"/"format"), CSS custom-property names, iconik field/view keys, and format
 * tokens (`{value}`, `{PenaltyType}`) all stay inline where they're actually wired up — moving
 * those here would risk someone "fixing a typo" in a string the backend/n8n contract depends on
 * byte-for-byte.
 *
 * Entries that are plain, static text are string literals. Entries that depend on runtime values
 * (a count, a source label, a field name) are functions — call them at the render site with the
 * live value rather than trying to template them here.
 */
export const SPOTTER_STRINGS = {
  header: {
    breadcrumbProduct: "Customer Portal",
  },

  /** `SpotterEditedTag`'s pill text — a row (field, glossary entry, or Changes-panel item) with an
   *  unsaved edit. */
  editedTag: "edited",
  newTag: "new",
  /** `SpotterAddedTag`/`SpotterRemovedTag` — Changes-panel item-level pills for one specific
   *  value gained/lost from a list (a source, a glossary term, an excluded tag, a routed FR
   *  person). */
  addedTag: "added",
  removedTag: "removed",

  /** `TokenChip.tsx`'s `NoTokenOptions` — shown in place of a `TokenCarousel`'s chips whenever a
   *  customer's in-progress typing has filtered the available tokens/fields down to none. */
  /** Shown instead of a Spotter screen whose configured iconik metadata view was deleted (or can
   *  no longer be read) in the customer's iconik. */
  configuredViewMissing:
    "The iconik metadata view this page works with no longer exists or can't be read. It may have been deleted in iconik. Contact CineSys support to set up a new one.",
  noTokenOptions: "No options",

  changesPanel: {
    title: "Changes",
    unsavedChangeCount: (count: number): string =>
      `${count} unsaved change${count !== 1 ? "s" : ""}`,
    noPendingChanges: "No pending changes.",
    saved: "Saved",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
  },

  viewPicker: {
    /** Fallback trailing unit on a picker row's "(done / total)" count when the caller doesn't
     *  specify one (`SpotterViewPickerOption.fieldCount.unit`) — see `SpotterViewPicker.tsx`. */
    defaultUnit: "fields",
  },

  /**
   * The unified Spotter field tab — replaces the old, now-deleted `mapping`/`glossary` string
   * namespaces (their `MappingScreen.tsx`/`GlossaryScreen.tsx` consumers are gone too, replaced
   * by `FieldsScreen.tsx`). Every
   * field, regardless of type (Mapped/Glossary), lives in ONE flat list — copy here is written to
   * be type-neutral where a string is shared (load/save errors, the search box, the two `Aside`
   * cards), with type-specific hints (mode picker, substitution matching, format templates,
   * glossary term editing) kept in their own sub-objects. Facial Recognition has its own tab and
   * its own `facialRecognition` string namespace below.
   */
  fields: {
    loadError: "Could not load the fields page. Please refresh to try again.",
    /** Distinguishes "you're not allowed in here" (backend's `spotter_access_denied` code, see
     *  `../accessError.ts`) from a generic load failure. */
    accessDeniedError:
      "You don't have access to Spotter. Ask your admin to add you to the configured Spotter access group.",
    saveError: "Could not save your fields. Please try again.",
    /** Rows with no source, rule, replacement or definition are left out of the save. */
    rowsNotSaved: (labels: string[]): string =>
      `Not saved, because nothing was filled in: ${labels.join(
        ", "
      )}. Add them again with a value, then save.`,
    /** Rows the API refused, e.g. a Conditional rule whose condition is invalid. */
    rowsRejected: (labels: string[]): string =>
      `Not saved, because the server rejected them: ${labels.join(
        ", "
      )}. Check each Conditional rule's condition and the fields it uses.`,
    /** Intro copy above the field list — spells out all five modes in a bit more detail than the
     *  Mode dropdown's own, deliberately short `extra` text (`modeOptions` below), each prefixed
     *  with that mode's `ModeDot` swatch (`fields/modeColors.ts`) so the same five-color coding
     *  shown on the dropdown and the row badge is legible here too. KEEP IN SYNC with
     *  `modeOptions`: same five modes, same order. Only touch this when a mode is added, removed,
     *  or renamed — not a place to casually reword for style. */
    note: (
      <>
        <div>
          Add the <strong>iconik fields</strong> you want Spotter to control.
          Each one gets a <strong>Mode</strong> that decides how Spotter fills
          it in — you can change it any time:
        </div>
        <ul
          style={{
            margin: "8px 0 0",
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <li>
            <ModeDot $mode="direct" />
            <strong>Direct</strong> — passes a Spotter source's value straight
            through, unchanged.
          </li>
          <li>
            <ModeDot $mode="match" />
            <strong>Substitute</strong> — swaps a source's values for your own,
            using a lookup table you define.
          </li>
          <li>
            <ModeDot $mode="format" />
            <strong>Format</strong> — combines one or more sources into a
            template you write.
          </li>
          <li>
            <ModeDot $mode="conditional" />
            <strong>Conditional</strong> — sets a value only when a rule you
            define matches.
          </li>
          <li>
            <ModeDot $mode="glossary" />
            <strong>Glossary</strong> — teaches Spotter's AI how to choose your
            drop-down values, so it learns your vocabulary instead of guessing.
          </li>
        </ul>
      </>
    ),
    progressLabel: "Fields mapped",
    searchPlaceholder: "Search your fields…",
    assetMetadataLabel: "Asset metadata",
    timedMetadataLabel: "Timed metadata",
    /** Comments lives under Timed metadata (iconik comments import into Premiere as markers via
     *  the Iconik panel, which time-based metadata tags do not). */
    commentDestinationLabel: "Comments",
    /** The Fields screen's view picker (`SpotterViewPicker`); only shown when the org has both an
     *  asset and a timed view. */
    viewPickerLabel: "Map into view",
    noFieldsInViewMessage:
      "No fields are mapped into this view yet. Use Add a field above to add one.",
    noSearchMatches: "No fields in this view match your search.",
    searchMatchesElsewhere: (count: number, viewLabel: string): string =>
      `${count} match${count !== 1 ? "es" : ""} in ${viewLabel}`,
    switchView: "Switch",
    /** The "Add Field" control — a `Select` styled to look like a green success-type `Button`
     *  (green border/text + a `PlusCircle` icon overlaid over its placeholder, see
     *  `FieldsScreen.tsx`'s `AddFieldSelectWrap`), not an actual `Button`. Choosing a field here
     *  resolves it immediately as a Mapped/Direct row (the newly-added row's own full-width Mode
     *  `Select` is where you then pick Glossary/etc. if that's not what you wanted) — this is
     *  simpler than an earlier round's two-step "pick a mode, then a field"
     *  flow: picking the field IS the add action, and the row it creates is immediately a normal,
     *  fully-editable row like any other. */
    addFieldButton: "Add a field",
    modeSelectLabel: "Mode",
    /** Shown above the Mode `Select` on any non-Glossary row (every other mode writes to the
     *  destination field) whenever that field's `field_type` is `drop_down`
     *  (`FieldsScreen.tsx`'s `renderFieldRow`) — unlike a tag_cloud field, a drop-down only
     *  accepts values that are already configured as options on it; iconik silently ignores a
     *  write of anything else, so a value that isn't already an option there just never gets set.
     *  Glossary is skipped since its terms already come from that same option list. Also reused
     *  as-is by the Facial Recognition tab (`facialRecognition` namespace below). */
    dropdownWarning:
      "This field is a Dropdown, so it only accepts values that you have configured as options in Iconik. Anything Spotter tries to write here that isn't one of those options won't get set — add it as an option on the field in Iconik first.",
    /** Row header's remove action — deletes the row outright (the field goes back to being an
     *  option in "Add a field"). Renamed from Field Mapping's old "Remove mapping" — a single flat
     *  list covering every mode reads oddly with a mapping-specific verb. */
    removeFieldButton: "Remove",
    expandRowTitle: "Expand",
    collapseRowTitle: "Collapse",
    sourceSelectLabel: "Source",
    /** Shown as the source `Select`'s `multiValueHint` — rendered INSIDE the control, after the
     *  existing source pills, only once at least one is already picked. Only outside "match"
     *  mode, since that's the only mode where more than one source can be picked. Mapped-family
     *  rows only — Glossary has no source concept. */
    sourceAddMoreHint: "Add a Spotter source...",
    previewLabel: "Preview",
    /** Mode picker copy — each option carries a short `label` plus an `extra` sub-description,
     *  rendered via the shared `Select`'s own `extra` slot. One flat list of 5 (Addison: "the
     *  user shouldn't really have to know that anything is like a separate concept" — no
     *  Mapped-vs-Glossary split surfaced anywhere, just "Mode"). `extra` is deliberately terse
     *  (it's a dropdown row, not a place to explain the mode) — the fuller explanation lives in
     *  `note` above. KEEP IN SYNC with `note`: same five modes, same order. Only touch this when
     *  a mode is added, removed, or renamed — not a place to casually reword for style. */
    modeOptions: {
      direct: {
        label: "Direct",
        extra: "Pass the source's value through unchanged",
      },
      match: {
        label: "Substitute",
        extra: "Swap values using your own lookup table",
      },
      format: {
        label: "Format",
        extra: "Combine sources into a template you write",
      },
      conditional: {
        label: "Conditional",
        extra: "Set a value when a rule matches",
      },
      glossary: {
        label: "Glossary",
        extra: "Teach the AI your own drop-down values",
      },
    },
    /** Shown in place of Glossary's normal `extra` copy above when the field isn't eligible for
     *  that mode (no live option list to match against) — the option still renders, just
     *  disabled, rather than disappearing from the list. */
    modeOptionUnavailableExtra:
      "Only available for fields with a curated option list",
    /** Disabled Mode options when switching would collide with the field's other row. */
    modeOptionGlossaryTakenExtra: "This field already has a Glossary row",
    modeOptionMappedTakenExtra: "This field already has a mapping row",
    /** Appended to a field's Add Field option label for the half being added. */
    addFieldMappingSuffix: "Mapping",
    addFieldGlossarySuffix: "Glossary",
    addFieldGlossaryGroup: "Glossary",
    sourcePlaceholder: "Choose a Spotter source",
    matching: {
      hintGlossary: (glossaryField: string | undefined): string =>
        `These are your own glossary terms (${glossaryField}) — the AI ` +
        "already returns them verbatim, so no matching is needed. Edit " +
        "that field's own Glossary-type row instead.",
      hintKnown: (count: number, sourceLabel: string): string =>
        `We know all ${count} values ${SPOTTER_WORDMARK} can emit for ${sourceLabel}. Type what you call each. Blank = keep ours.`,
      hintUnknown: (sourceLabel: string): string =>
        `No fixed list for ${sourceLabel} — add rows for values you want swapped. Anything unlisted passes through unchanged.`,
      addRowFromPlaceholder: "our value",
      addRowToPlaceholder: "your value",
      addRowButton: "Add",
    },
    formatting: {
      templateLabel: "Format",
      templatePlaceholderSingle: "e.g. {value}",
      templatePlaceholderMulti: (first: string, last: string): string =>
        `{${first}} ({${last}})`,
    },
    direct: {
      merged:
        "Values are combined and deduplicated. Existing values will not be overwritten.",
    },
    /** Conditional-mode rule editor — first-match-wins ordered list of `when` -> `then.set` rules.
     *  `when` is plain Lucene syntax (no Monaco/syntax highlighting) over any Spotter source
     *  field for the org — a blank `when` always matches, so a rule with nothing typed can serve
     *  as the trailing fallback default. */
    conditional: {
      hint: "Each rule below is checked in order — the first one whose condition matches sets this field's value. Leave a rule's condition blank to use it as a fallback default.",
      whenLabel: "When",
      whenSyntaxDocsLabel: "syntax",
      syntaxHelpTitle: "Condition syntax",
      syntaxHelpIntro:
        "A condition compares one of your source fields to a value. Here's what you can type:",
      whenPlaceholder: "e.g. PlayType:Rush AND Yards:>=10",
      setLabel: "Set value to",
      setPlaceholder: "value to write",
      modeLabel: "Mode",
      modeAppend: "Append",
      modeOverwrite: "Overwrite",
      addRuleButton: "Add rule",
      removeRuleButton: "Remove rule",
      fieldsHintLabel: "Insert a field",
      noFieldsAvailable: "No source fields are available for this org yet.",
      checking: "Checking…",
      validSyntax: "Syntax is valid",
      invalidSyntax: "Invalid condition syntax",
    },
    /** Glossary-type row copy — moved from the old `glossary` namespace verbatim (same term/
     *  definition editing UI, now inline in a unified row instead of its own screen). */
    glossary: {
      termLabel: "Your term",
      definitionLabel: "Tell the AI when to apply it",
      definitionPlaceholder:
        "e.g. Apply when the QB reads a defender after the snap and chooses to hand off or throw.",
    },
    /** Per-tag "do not map" (Seth Tanner's ask) — type-agnostic, shown as the LAST input, in its
     *  own visually separated section (a top-border "footer", `ExcludedTagsWrap`), on any row
     *  whose destination field has a live iconik option list. A multi-select rather than a
     *  per-tag checkbox list — picking a term here removes it from that field's own editable
     *  list too (Glossary's term/definition rows). "Tags" everywhere except Glossary, which
     *  calls its own option-list values "terms" elsewhere on the same row (`glossary.termLabel`)
     *  — `FieldsScreen.tsx`'s `renderExcludedTagsEditor` picks the right copy pair by type.
     *  Stored/exposed as-is; enforcing the exclusion downstream (e.g. what n8n actually writes)
     *  is n8n's own responsibility, not this app's — this app only guarantees an excluded term's
     *  own definition is never SENT (see `FieldsScreen.tsx`'s `handleSave`). */
    excludedTags: {
      tagsLabel: "Excluded tags",
      termsLabel: "Excluded terms",
      tagsPlaceholder:
        "Prevent Spotter from ever applying these tags, regardless of what it would otherwise produce",
      termsPlaceholder:
        "Prevent Spotter from ever applying these terms, regardless of what it would otherwise produce",
    },
    suppressQbOnPass: "Suppress QB tag on passing plays",
    warmup: {
      label: "Warm-up segment",
      minLabel: "Min minutes before kickoff",
      minPlaceholder: "e.g. 30",
      maxLabel: "Max minutes before kickoff",
      maxPlaceholder: "e.g. 90",
      invalid:
        "Enter whole minutes from 1 to 180, with the min no larger than the max.",
      saveBlocked: "Fix the warm-up range (1 to 180 minutes) to save.",
      saveBlockedElsewhere: (viewLabel: string): string =>
        `Fix the warm-up range (1 to 180 minutes) on a ${viewLabel} field to save.`,
      goToField: "Go to field",
    },
    /** Changes-panel child-item copy for a field group — one item per distinct edit detected
     *  between a field's saved baseline and its current local state (`FieldsScreen.tsx`'s
     *  `fieldChangeItems`), so the panel reads as a real diff instead of a generic "edited" line.
     *  The New/Edited tag itself lives on the parent group row, not these items — see
     *  `SpotterChangeGroup.isNew`. One value gained/lost from a list (a source or a term) is its
     *  own item, bare-labeled, carrying an added/removed pill. An item that's actually about a
     *  mode (added as, removed as, switched between) carries the mode-color `TypeTag` badge the
     *  row list uses instead — the mode name isn't repeated in these strings since the badge
     *  already shows it. */
    changes: {
      added: (detail?: string): string =>
        `Added${detail ? ` (${detail})` : ""}`,
      removed: "Removed",
      modeChanged: "Mode changed",
      formatTemplateEdited: "Format template edited",
      substituteValuesEdited: "Substitution values edited",
      conditionalRulesEdited: "Conditional rules edited",
    },
    noFieldsMessage:
      "No fields are configured yet. Use Add a field above to add one.",
    preview: {
      cardTitle: "Preview",
      empty: "Add a field to see the tags that would land on a real play.",
    },
    unusedSources: {
      cardTitle: `Unused ${SPOTTER_WORDMARK} sources`,
      allUsed: "all used",
      empty: `Every ${SPOTTER_WORDMARK} field is feeding something.`,
    },
  },

  /**
   * The Facial Recognition tab (`FrScreen.tsx`) — every row is a destination field with the
   * org's NAMED FR persons routed to it, no Mode picker (a row is definitionally Facial
   * Recognition). Built on the same shared row shell/add pattern as `fields` above
   * (`CollapsibleFieldRow`, `AddOptionSelect`) — copy here mirrors that namespace's shape where
   * the two screens share a concept (search, add, remove, excluded tags reused directly from
   * `fields.excludedTags`/`fields.dropdownWarning` rather than duplicated here).
   */
  facialRecognition: {
    loadError:
      "Could not load Facial Recognition. Please refresh to try again.",
    accessDeniedError:
      "You don't have access to Spotter. Ask your admin to add you to the configured Spotter access group.",
    saveError: "Could not save your changes. Please try again.",
    intro:
      "Add any iconik field you want Spotter's facial recognition to add recognized faces to. Choose which recognized people get routed to each field.",
    searchPlaceholder: "Search fields or people…",
    addFieldButton: "Add a field",
    removeFieldButton: "Remove",
    expandRowTitle: "Expand",
    collapseRowTitle: "Collapse",
    /** Fixed row badge — every row on this tab is Facial Recognition, so unlike `fields.modeOptions`
     *  this isn't driven by a per-row value. */
    badgeLabel: "Facial Recognition",
    hint: "Spotter will tag recognized faces onto this field.",
    personsLabel: "Recognized people",
    personsPlaceholder: "Choose named people to route here",
    /** Collapsed-row subtitle — how many people are currently routed to this field. */
    peopleCount: (count: number): string =>
      `${count} ${count === 1 ? "person" : "people"} routed`,
    noFieldsMessage:
      "No fields are configured yet. Use Add a field above to add one.",
    changes: {
      /** Shown in the changes panel for a person who was unassigned entirely (removed from every
       *  field's multi-select, not moved to a different one). */
      unassignedLabel: "Unassigned",
      /** A routed person moved from one destination field to another — not a plain add/remove, so
       *  unlike a newly-routed or unassigned person (bare name + pill), this stays a descriptive
       *  line. */
      personMoved: (name: string, fromLabel: string): string =>
        `${name} — moved from ${fromLabel}`,
    },
  },

  gamebook: {
    loadError: "Could not load the gamebook. Please refresh to try again.",
    notConfigured:
      "No NFL club is configured for this account yet. Contact CineSys to finish setup.",
    seasonLabel: "Season",
    weekLabel: "Week",
    seasonUnit: (season: number): string => `${season} Season`,
    generateTitle: (weekLabel: string): string =>
      `${weekLabel} — no gamebook yet`,
    generateBody:
      "This game hasn't been run through the stats pipeline yet. Generate builds this " +
      `week's gamebook (and freezes its play UIDs) in the database — this can take a few ` +
      "minutes.",
    generateButton: "Generate gamebook",
    generating: "Generating…",
    generateError: "Could not start the gamebook generation. Please try again.",
    generateSuccess:
      "Started — this can take a few minutes. This tab will update on its own once it's ready.",
    generateTimeout:
      "Still working — this is taking longer than usual. Refresh to check on it.",
    refreshButton: "Refresh gamebook",
    refreshing: "Refreshing…",
    refreshError: "Could not start the gamebook refresh. Please try again.",
    refreshSuccess:
      "Refreshing — this can take a few minutes. This tab will update on its own once it's ready.",
    refreshTimeout:
      "Still working — this is taking longer than usual. Refresh to check on it.",
    refreshDisabledTooltip: "A refresh is already in progress for this week.",
    opponentPending: "— pending schedule lookup —",
    opponentLine: (home: boolean, opponentName: string): string =>
      `${home ? "vs" : "@"} ${opponentName}${home ? "" : " (away)"}`,
    toolbar: {
      allQuarters: "All",
      overtime: "OT",
      quarter: (n: number): string => `Q${n}`,
      searchPlaceholder: "Find a UID or search the play…",
      scoringToggle: "Scoring",
      penaltiesToggle: "Penalties",
      criticalToggle: "Critical",
      /** Toolbar's shown-count line — rendered as separate pieces (not one template string) so
       *  `GamebookScreen.tsx` can bold just the leading number, not the whole line. */
      shownWord: "shown",
      playsWord: "plays",
      downloadCsv: (rowCount: number): string => `Download CSV · ${rowCount}`,
    },
    columns: {
      uid: "UID · Play",
      situation: "Situation",
      /** Spells out offense/defense directly (rather than the old "Poss.") so the expanded row's
       *  detail panel doesn't need to restate "on offense → defense" as its own field. Rendered
       *  ALL CAPS via the column header's own `text-transform` (not hardcoded here) so this same
       *  string still reads fine anywhere else it's reused. */
      possession: "Off ⏵ Def",
      play: "What happened",
      score: "Score",
      flags: "Flags",
    },
    noPlays: "No plays match these filters.",
    scoreFlag: "SCORE",
    penaltyFlag: "FLAG",
    keyFlag: "KEY",
    detail: {
      penalty: "Penalty",
      importance: "Importance",
      /** Raw source values (Down/DistanceToGo/LineOfScrimmage), not humanized — Addison: shown
       *  here (not the collapsed row) exactly as the source would map, not "First down"/"5 to
       *  go". */
      down: "Down",
      distanceToGo: "Distance to go",
      lineOfScrimmage: "Line of scrimmage",
      presnap: "Pre-snap elements",
    },
  },

  filmsGround: {
    loadError: "Could not load Films Ground. Please refresh to try again.",
    /** The states a Films Ground/Top Films review can be in — rendered as
     *  `ControlRoomScreen.tsx`'s `ReviewStatusBadge` on each rail row. */
    jobStatusLabel: {
      awaiting_start: "Awaiting start",
      starting: "Starting",
      trigger_failed: "Start failed",
      analyzing: "Analyzing",
      needs_confirmation: "Ready to confirm",
      submitted: "Submitted",
      /** A finished Top Films run (its anchor-job stays `submitted` underneath). */
      done: "Done",
    },
    phaseA: {
      introTitle: "First, tell us where the game begins",
      introBody:
        "This ground footage has no synced timecode, so Spotter can't tell warmups from live " +
        "play. Point it at the first clip with a live snap and its play — everything before is " +
        "pregame and skipped. That kicks off the analysis; you'll confirm the anchors next.",
      stepLabel: "Step 1 · Set where the game begins",
      startBoundaryTitle: "Start of game action",
      startBoundarySubtitle: "which clip has the first live snap?",
      startBoundaryChip: "Start boundary",
      /** The start-boundary card's own longer prompt — distinct from `introBody` above (which
       *  stays short, for the page-level intro card). Lifted/adapted from the mockup's own
       *  `.a-prompt.full` copy on the same card. */
      startCardPrompt: (
        <>
          Spotter can't know where warmups end and the game begins in a ground
          collection — <b>you set that line</b>. Browse the collection, find the
          first clip that shows a <b>live snap</b>, and link it below with its
          play from the gamebook. Everything recorded before this clip is then
          tagged <b>pregame</b> automatically.
        </>
      ),
      openCollectionLink: "Open collection in iconik",
      /** Same link row, for a job sourced from a saved search rather than a collection (Monday
       *  12871494046, `source` contract round) — never both at once, `job.source.kind` picks. */
      openSavedSearchLink: "Open saved search in iconik",
      openGamebookLink: "Open gamebook",
      startUrlLabel: "First game-action clip",
      startUrlRequired: "iconik asset URL",
      startUrlPlaceholder: "https://app.iconik.io/asset/…",
      startUrlInvalid: "Paste a valid iconik asset link to continue.",
      pickPlayPrompt:
        "Now pick its play — the opening clip is almost always in the first ten. This sets the " +
        "floor and kicks off analysis of the rest of the reel.",
      submitStartError: "Could not set the game start. Please try again.",
      railStepTitle: "Step 1 of 2 · Game start",
      railStepBody:
        "Point Spotter at the first clip with a live snap and pick its play. Everything before " +
        "it is pregame and skipped — this kicks off the analysis.",
      railNextTitle: "What happens next",
      railNextBody:
        "Spotter analyzes the reel from your first play forward, then notifies you to come " +
        "back and confirm the anchors — no waiting around.",
      startingTitle: "Starting analysis…",
      startingBody:
        "Sending your game start to Spotter. This tab updates on its own — you can keep " +
        "working or come back later.",
      startFailedTitle: "Spotter couldn't start the analysis",
      startFailedBody: (reason: string): string =>
        `${reason.replace(/[.\s]+$/, "")}. Set the game start again to retry.`,
      startFailedToast: (gameLabel: string): string =>
        gameLabel
          ? `Spotter couldn't start the analysis for ${gameLabel}.`
          : "Spotter couldn't start the analysis.",
      startFailedToastLink: "Open the Control Room",
      startFailedToastDismiss: "Dismiss",
      analysisUnderwayTitle: "Game start set — analysis underway",
      analysisUnderwayBody:
        "Spotter is analyzing your reel from the first play forward. Come back any time — this " +
        "tab updates on its own once anchors are ready to confirm.",
      analysisTimeout:
        "Still working — this is taking longer than usual. Refresh to check on it.",
      autoSubmittedTitle: "Anchors set automatically",
      autoSubmittedBody:
        "Nothing further needed here — analysis resolved this collection's anchors on its own.",
    },
    /** Top Films' overrides onto `phaseA` above — same start-clip UI (card, links, play picker),
     *  but the copy that promises a Step 2 confirm is wrong for a job that doesn't have one.
     *  `AnchorReviewPanel.tsx` merges this over `phaseA` for a `mode: "top_films"` job; every key
     *  not listed here (the start card itself, its fields, its link labels) is identical between
     *  the two and stays in `phaseA` alone. */
    phaseATopFilms: {
      introTitle: "First, tell us where the game begins",
      introBody:
        "This ground footage has no synced timecode, so Spotter can't tell warmups from live " +
        "play. Point it at the first clip with a live snap and its play — everything before is " +
        "pregame and skipped. That's the only step; analysis runs on its own from there.",
      stepLabel: "Set where the game begins",
      railStepTitle: "Game start",
      railNextTitle: "What happens next",
      railNextBody:
        "Spotter analyzes the reel from your first play forward — nothing else to confirm here.",
      autoSubmittedTitle: "Start clip confirmed",
      autoSubmittedBody:
        "Nothing further needed here — Spotter is analyzing this collection from your selected " +
        "start.",
    },
    phaseB: {
      introTitle: "Confirm this collection's anchor plays",
      introBody: (totalAnchors: number): string =>
        `This ground footage has no synced timecode, so Spotter can't place clips on the game ` +
        `clock automatically. Confirm or correct each of the ${totalAnchors} anchor clips below; ` +
        `Spotter then fills every play between your anchors in recording order.`,
      pendingSectionLabel: "Needs your confirmation",
      /** Swaps in for `pendingSectionLabel` on the CURRENT anchor once it already has a draft
       *  resolution (picked, but not yet submitted) — see `AnchorStage.tsx`'s `resolved` prop. */
      confirmedSectionLabel: "Confirmed",
      /** The game-start boundary's own leading reel frame (`ReelStrip.tsx`) — a real, non-
       *  interactive card at the front of the carousel, not a subtitle above it. `startRowText` is
       *  its accessible name (the full sentence); `startFrameLabel` is the short visible label the
       *  narrow card itself has room for. */
      startRowText: (summaryText: string): string =>
        `Game start (set in Step 1) — ${summaryText}`,
      startFrameLabel: "Game start",
      anchorPrompt:
        "Watch the clip, then pick its play from the gamebook. Spotter's best guess is highlighted.",
      manualUidLabel: "Play not listed?",
      manualUidPlaceholder: "P000",
      manualUidSet: "Set",
      manualUidInvalid: "Enter a play UID like P031.",
      outOfOrderBadge: "Out of order",
      outOfOrderBanner: (count: number): string =>
        `${count} anchor${
          count === 1 ? "" : "s"
        } out of order — fix before submitting`,
      /** Header-mounted progress meter's leading label (design A4 — moved out of the old sticky
       *  submit rail into the page header, matching `FieldsScreen.tsx`'s own `progressLabel`). */
      headerProgressLabel: "Anchors confirmed",
      submitButton: "Submit collection to Spotter",
      submitting: "Submitting…",
      submitAllConfirmed: "Ready to send",
      submitRemaining: (count: number): string =>
        `Confirm all anchors to submit (${count} left)`,
      submitSuccess:
        "Submitted — anchors locked. Spotter is filling in every play between them in recording order.",
      submitError: "Could not submit — please try again.",
      alreadySubmitted: "Already submitted — anchors are locked.",
      openVideo: "Play",
      closeVideo: "Close",
      /** Player-corner toggle that enlarges the video and lets the page scroll. */
      videoSizeAria: "Larger video",
      videoSizeLarger: "Larger",
      videoSizeNormal: "Normal",
      /** `ClipNavCard`'s own caption line (`AnchorStage.tsx`) — restored wording Addison
       *  specifically liked from an earlier version of this copy ("Unsure if this is the right
       *  clip, you can choose a different one"), re-added above the arrow row per that same
       *  follow-up ask. Left unchanged by the later "less text" cleanup — that pass removed the
       *  OTHER, redundant messages around it (the separate substituted-clip sentence and the
       *  boundary "no earlier/later clip" paragraphs, now a disabled-button `title` tooltip via
       *  `neighborNoEarlierClip`/`neighborNoLaterClip` below) rather than this one. */
      neighborStepperHint:
        "Unsure this is the right clip? You can choose a different one.",
      neighborLoadingLabel: "Loading…",
      neighborOwnClipLabel: "This clip",
      neighborSubstitutedLabel: "Using a substituted clip",
      neighborUndoSubstitution: "Use Spotter's original clip",
      neighborNoEarlierClip: "No earlier clip in this reel.",
      neighborNoLaterClip: "No later clip in this reel.",
      neighborShowAllToggle: "Show all clips (incl. non-play footage)",
      neighborPrevAnchorAria: "Previous anchor",
      neighborNextAnchorAria: "Next anchor",
      substitutedBadge: "Different clip",
      /** The "Films — ground" mode chip on the full-page anchor editor's stage. Slow-mo band jobs
       *  use `phaseBSlowmoBand.bandBadge` ("Slow-mo band") in this slot instead. */
      modeChipLabel: "Films — ground",
      anchorCounter: (n: number, total: number): string =>
        `Anchor ${n} / ${total}`,
      reelStripAria: "Anchor reel",
      reelConfirmedAria: (clipLabel: string, uid: string): string =>
        `${clipLabel} — confirmed to ${uid}`,
      reelOutOfOrderAria: (clipLabel: string, uid: string): string =>
        `${clipLabel} — confirmed to ${uid}, out of order`,
      reelPendingAria: (clipLabel: string): string =>
        `${clipLabel} — not yet confirmed`,
      breadcrumbSeparator: "›",
      gamebookHeading: "Gamebook",
      gamebookColumnUid: "UID · Play",
      gamebookColumnSituation: "Situation",
      gamebookColumnWhatHappened: "What happened",
      gamebookColumnFlags: "Flags",
      gamebookPickTag: "Spotter's pick",
    },
    /** Slow-mo band overrides onto `phaseB` above — `AnchorConfirmContent.tsx` merges this over
     *  `phaseB` for a `reviewMode: "slowmo_band"` job. Only framing copy changes; the anchor
     *  stage (`AnchorStage.tsx`) stays generic since a band highlight is an ordinary anchor row,
     *  resolved the same way a plain Films Ground one is. Every key not listed here stays
     *  whatever `phaseB` already says. */
    phaseBSlowmoBand: {
      introTitle: "Confirm this slow-motion band's highlights",
      introBody: (totalAnchors: number): string =>
        `Spotter pulled ${totalAnchors} slow-motion highlight${
          totalAnchors === 1 ? "" : "s"
        } from this band. Confirm or correct which play each one shows below — the boundary ` +
        `clips mark where this band starts and ends in the reel.`,
      bandBadge: "Slow-mo band",
      bandBoundaryStartLabel: "Preview band start",
      bandBoundaryEndLabel: "Preview band end",
      submitButton: "Submit band to Spotter",
      submitSuccess: "Submitted — this band's highlights are locked.",
    },
  },

  rename: {
    loadError:
      "Could not load the file rename settings. Please refresh to try again.",
    accessDeniedError:
      "You don't have access to Spotter. Ask your admin to add you to the configured Spotter access group.",
    saveError: "Could not save the file rename settings. Please try again.",
    originalNameFieldRequiredError:
      "Choose an Original Name Field before saving.",
    note: (
      <>
        Set a <strong>File Name Template</strong> for any footage category where
        Spotter should rename the physical file once tagging finishes. The
        file's previous name is always written back to the{" "}
        <strong>Original Name Field</strong> below.
      </>
    ),
    templatesSectionTitle: "File Name Templates",
    /** `CollapsibleFieldRow`'s own `removeLabel` prop already defaults to this — kept explicit
     *  here for clarity rather than relying on the default. */
    removeTemplateButton: "Remove",
    allCategoriesAddedMessage: "All categories configured",
    addTemplateButton: "Add a new file name template",
    /** Title shown on an unresolved template card, before a category's been picked. */
    newTemplateTitle: "New Template",
    addTemplateSelectLabel: "Category",
    addTemplatePlaceholder: "Choose a category",
    templateLabel: "Filename template",
    templateRemovedLabel: "Template removed",
    templatePlaceholder: "e.g. {GameID}_{ClipNumber}",
    previewLabel: "Resolves to",
    optionsTitle: "Options",
    replaceAssetTitleLabel: "Replace Asset Title to match the new filename",
    includeFileExtensionInTitleLabel:
      "Include the file extension in the Asset Title",
    originalNameFieldLabel: "Original Name Field",
    originalNameFieldPlaceholder: "Choose a metadata field",
    originalNameFieldDescription:
      "The file's current name is saved here before it's renamed, so nothing is lost.",
    useMetadataValuesLabel: "Use metadata Values instead of Labels",
    expandRowTitle: "Expand",
    collapseRowTitle: "Collapse",
  },

  /** Round 2 (Monday 12650205737): rebuilt to match `portal-usage-mock.html` (Drive) in full —
   *  copy below is lifted from that mock verbatim where it's customer-facing (the mock's own
   *  "Dev note" callout is explicitly NOT customer-facing copy and isn't reproduced here). */
  usage: {
    loadError: "Could not load usage. Please refresh to try again.",
    accessDeniedError:
      "You don't have access to Spotter. Ask your admin to add you to the configured Spotter access group.",
    /** Shown instead of the meter cards when the org hasn't been onboarded in Iconik Manager yet
     *  (`GET /usage` returns an all-zero response with a null period — see
     *  `apps/customer-api/src/services/spotterUsage.ts`'s doc comment) — a normal empty state, not
     *  a load failure, mirroring `gamebook.notConfigured`. */
    notConfigured:
      "No billing period is set up for this account yet. Contact CineSys to finish setup.",
    pageTitle: "Footage Usage",
    /** `Intl.DateTimeFormat`-formatted `period_start`, e.g. "This period · since Jul 1, 2026". */
    periodSince: (start: string): string => `This period · since ${start}`,
    subNote:
      "Hours of footage Spotter has analyzed for you this period, by type. Your account keeps " +
      "analyzing continuously — this is here so you always know where your usage stands.",
    practiceTitle: "Practice Footage",
    practiceDescriptor: "camp · walkthrough · intrasquad",
    practiceCaption: "Practice-type clips processed this period.",
    gameTitle: "Game Footage",
    gameDescriptor: "broadcast · all-22 · sideline · iso",
    gameCaption: "Game-type clips processed this period.",
    hoursUnit: "hours analyzed",
    sparklineThisWeek: "this week",
    sparklineWeeksAgo: (weeks: number): string =>
      weeks === 1 ? "1 wk ago" : `${weeks} wks ago`,
    recentActivityTitle: "Recent Activity",
    recentActivityCount: (count: number): string =>
      `last ${count} clip${count === 1 ? "" : "s"}`,
    recentActivityEmpty: "No clips analyzed yet this period.",
    columnAnalyzed: "Analyzed",
    columnType: "Type",
    columnDuration: "Duration",
    columnAsset: "Asset",
    columnAssetOpen: "Open",
    practiceTag: "Practice",
    gameTag: "Game",
  },

  /** Control Room tab (Monday 12911625908) — every job's live status, fed by n8n's status pushes
   *  (Monday 12908478970). `modeLabels`/`stageLabels` mirror the customer's own spec (§5/§6
   *  suggested labels) verbatim — keep in sync if he ever adds a `mode`/`stage`. */
  controlRoom: {
    loadError: "Could not load the control room. Please refresh to try again.",
    accessDeniedError:
      "You don't have access to Spotter. Ask your admin to add you to the configured Spotter access group.",
    empty: "No jobs yet. This fills in as soon as a run starts.",
    modeLabels: {
      game: "Game",
      sideline: "Sideline sound",
      broadcast: "Broadcast",
      films_ground: "Films — ground",
      top_films: "Films — top",
      practice: "Practice",
      intersquad: "Intersquad",
      wire: "Wire",
      general: "General",
      subclips: "Subclips",
      unknown: "Unknown",
    },
    stageLabels: {
      queued: "Queued",
      processing_footage: "Processing footage",
      identifying_plays: "Identifying plays",
      tagging: "Tagging",
      creating: "Creating",
      cutting: "Cutting",
      scanning: "Scanning",
      done: "Done",
    },
    /** Shown in place of a stage label when `latestStage` is still `null` — the job exists (it's
     *  had at least one push) but hasn't reached its first main stage yet. */
    notYetQueued: "Starting…",
    needsInputLabel: "Needs your input",
    errorLabel: "Error",
    /** A finished run where some items failed, or that reported an error after finishing. */
    warningLabel: "Done with warnings",
    /** A running job with no update for a long while. Not a failure. */
    stalledLabel: "Stalled",
    /** Rail row's relative-time line, e.g. "3m ago" / "just now" — see `ControlRoomScreen.tsx`'s
     *  `formatRelativeTime`. */
    justNow: "just now",
    /** The Job column's target subline (Monday 12871494046, "add the collection or saved search
     *  name next to the job id") — `openInIconikTitle` labels the external link for a
     *  `collection`/`saved_search` target (name comes from the job data itself, already resolved
     *  server-side) and folds the raw job id into the same tooltip — a nested `title` on the
     *  link would otherwise shadow the row's own, leaving the job id unreachable on hover for
     *  every resolved row; `assetsCount` covers the `assets` target, which has no name/link to
     *  show. */
    openInIconikTitle: (jobId: string): string =>
      `Open in iconik — job ${jobId}`,
    assetsCount: (n: number): string => `${n} asset${n === 1 ? "" : "s"}`,
    /** The parent-collection slot's literal text for a saved-search row (Monday 12937644849) —
     *  `AnchorJobPicker.tsx`'s collection-plus-parent shape, saved searches have no parent so this
     *  fills that slot instead. */
    savedSearchLabel: "Saved Search",
    /** The rail's four section headers (design A4, Monday 12936679727) — replaces the old filter
     *  chips (`tabs`, removed) AND the org-wide anchor-review tab strip in one nav. Keyed the same
     *  as `ControlRoomTabKey` minus `all` (the rail has no "everything" section — see
     *  `ControlRoomScreen.tsx`'s `RAIL_SECTION_ORDER`). `processing`'s copy is deliberately new
     *  ("In progress", not the old chip's "Processing") — Addison's own wording for this design. */
    railSectionLabels: {
      needsYou: "Needs you",
      processing: "In progress",
      done: "Done",
      hidden: "Hidden",
    },
    /** The rail's own accessible name (`RailPane`'s `aria-label`), also the mobile drawer
     *  trigger's button label and the drawer panel's own heading — one short label reused in all
     *  three, since they're all naming the same thing. */
    jobsListLabel: "Jobs",
    /** `SpotterHeader.tsx`'s Control Room tab dot — its accessible name, since the dot itself has
     *  no visible text (Addison: "show a little needs attention icon on the control room tab...
     *  even when you're on a different tab?"). */
    needsAttentionTabDot: "Needs your attention",
    /** The mobile rail drawer's own close control (`RailDrawer`, below `RAIL_BREAKPOINT`). */
    closeButton: "Close",
    expandSectionTitle: "Expand section",
    collapseSectionTitle: "Collapse section",
    /** Right pane's "nothing selected" state (design A4) — a real state, not a blank pane: says
     *  how many jobs in the rail's own Needs You section are waiting on a customer action. */
    nothingSelectedTitle: "Select a job",
    nothingSelectedBody: (waitingCount: number): string =>
      waitingCount > 0
        ? `${waitingCount} job${waitingCount === 1 ? "" : "s"} need${
            waitingCount === 1 ? "s" : ""
          } your attention.`
        : "Nothing needs your attention right now — pick any job from the list at left.",
    /** The right pane's "nothing selected" state when the org-wide list itself failed to load
     *  (round-4 review finding) — `nothingSelectedBody` above reads as "the list is empty", which
     *  is actively misleading right next to the rail's own `loadError` banner during a real
     *  failure. Distinct copy, not a reuse of `loadError`: that one names the control room by
     *  name for the rail's own banner; this one is scoped to "your jobs" since it sits inside the
     *  review pane, not the rail. */
    nothingSelectedLoadErrorTitle: "Couldn't load your jobs",
    nothingSelectedLoadErrorBody:
      "The jobs list failed to load — see the error at left, or refresh to try again.",
    /** The round icon-only hide/unhide control's tooltip (Monday 12921148830, Seth: "get rid of"
     *  jobs that hang forever; wording set by Monday 12936658014 — no `title` attribute, the
     *  tooltip alone is this control's accessible name). */
    hideJobTooltip: "Hide Job",
    unhideTooltip: "Unhide Job",
    /** Monday 12991249614 — shown in place of the generic status pill/note on a row that has no
     *  review to open BECAUSE a duplicate run replaced it (`job.superseded`), rather than leaving
     *  the row unexplained. Says "source", not "collection" (code-review catch, round 1) — the
     *  supersede pass this reflects (`filmsGroundStore.ts`'s `jobSourceKey`) scopes to a
     *  collection OR a saved search, and a saved-search-sourced duplicate would make "collection"
     *  wrong. Wording deliberately doesn't say "cancel" or "dismiss" — there's nothing to undo,
     *  the newer run already is the live one. */
    supersededBadge: "Superseded",
    supersededNote:
      "A newer run was started for the same source — nothing to open here.",
    /** The right pane's failure report for a run that stopped. */
    failureReport: {
      title: "Run stopped",
      lateTitle: "Finished with an error",
      whatHappened: "What happened",
      whatToDo: "What to do",
      links: "Links",
      generating: "Reviewing the failure and generating a report…",
      noDetails: "No details were sent for this run.",
      failedAt: (when: string): string => `Failed ${when}`,
      lateFailedAt: (when: string): string => `Error reported ${when}`,
      reference: "Reference",
      viewReview: "View play confirmations",
      backToReport: "Back to the failure report",
    },
    offer: {
      title: "Suggested fix",
      excluded: "Left out of the rerun",
      effect: "What happens",
      approve: "Approve and run",
      approving: "Approving…",
      showAll: (n: number): string => `Show all ${n}`,
      showLess: "Show fewer",
      inProgress:
        "This fix is already being approved. Check the Control Room for the new job.",
      changed:
        "The suggested fix has changed. The new one is shown now. Review it before approving.",
      notConfigured:
        "Approving fixes isn't set up yet for your account. Contact support.",
      wrongAccount:
        "This fix belongs to a different account. Switch accounts and try again.",
      signInAgain:
        "Your session isn't valid for approving. Please sign in again.",
      used: "This fix has already been approved.",
      expired: "This fix offer has expired.",
      unconfirmed:
        "We couldn't confirm the approval. Check the Control Room for a new job before trying again.",
      failed: "The fix couldn't be approved. Try again.",
    },
  },
} as const;
